import { useDropzone } from 'react-dropzone';
import Button from '../../components/Button';
import styles from './style.module.scss';
import { UploadIcon } from '../../assets/icons';
import "react-image-crop/dist/ReactCrop.css";
import { useDebounceEffect } from "../../hooks/useDebounceEffect";
import { canvasPreview } from "./canvasPreview";
import React, { useState, useRef } from "react";
import ReactCrop, {
    Crop,
    PixelCrop,
    centerCrop,
    makeAspectCrop,
} from "react-image-crop";

function centerAspectCrop(
    mediaWidth: number,
    mediaHeight: number,
    aspect: number,
) {
    return centerCrop(
        makeAspectCrop(
            {
                unit: "%",
                width: 90,
            },
            aspect,
            mediaWidth,
            mediaHeight,
        ),
        mediaWidth,
        mediaHeight,
    );
};

interface Props {
    aspect?: number;
    onSave: (imgUrl: string) => void;
    fileTypes: string[];
    showPreview?: boolean;
    fileObjectURL?: string | null;
};

function UploadBox(props: Props) {
    const {
        aspect,
        onSave,
        fileTypes,
        fileObjectURL,
        showPreview = true,
    } = props;

    const [imgSrc, setImgSrc] = useState(fileObjectURL || '');
    const previewCanvasRef = useRef<HTMLCanvasElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);
    const [crop, setCrop] = useState<Crop>();
    const [completedCrop, setCompletedCrop] = useState<PixelCrop>();

    const { getRootProps, getInputProps, open } = useDropzone({
        noClick: true,
        accept: { 'image/': fileTypes },
        onDrop: acceptedFiles => {
            onSelectFile(acceptedFiles)
        },
    });

    function onSelectFile(files: File[]) {
        if (files && files.length > 0) {
            setCrop(undefined);
            const reader = new FileReader();
            reader.addEventListener("load", () =>
                setImgSrc(reader.result?.toString() || ""),
            );
            reader.readAsDataURL(files[0]);
        }
    }

    function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
        const { width, height } = e.currentTarget;
        setCrop(centerAspectCrop(width, height, 1));
    }

    async function onSaveCrop() {
        const image = imgRef.current;
        const previewCanvas = previewCanvasRef.current;
        if (!image || !previewCanvas || !completedCrop) {
            throw new Error("Crop canvas does not exist");
        }

        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;

        const offscreen = new OffscreenCanvas(
            completedCrop.width * scaleX,
            completedCrop.height * scaleY,
        );
        const ctx = offscreen.getContext("2d");
        if (!ctx) return

        ctx.drawImage(
            previewCanvas,
            0,
            0,
            previewCanvas.width,
            previewCanvas.height,
            0,
            0,
            offscreen.width,
            offscreen.height,
        );

        const blob = await offscreen.convertToBlob({
            type: "image/png",
        });

        onSave(URL.createObjectURL(blob));
    }

    useDebounceEffect(
        async () => {
            if (
                completedCrop?.width &&
                completedCrop?.height &&
                imgRef.current &&
                previewCanvasRef.current
            ) {
                canvasPreview(
                    imgRef.current,
                    previewCanvasRef.current,
                    completedCrop,
                );
            }
        },
        100,
        [completedCrop],
    );

    return (
        <>
            {!imgSrc ? (
                <div {...getRootProps({ className: styles.uploadBox })} onClick={open}>
                    <input {...getInputProps()} />
                    <UploadIcon />
                    <span className={styles.uploadBox_title}>
                        Drop your files here or <b>browse</b>
                    </span>
                    <span className={styles.uploadBox_description}>
                        JPG or PNG, file size no more than 10MB
                    </span>
                </div>) :
                (<>
                    {!!imgSrc && (
                        <div className={styles.mainImage}>
                            <ReactCrop
                                crop={crop}
                                onChange={(_, percentCrop) => setCrop(percentCrop)}
                                onComplete={(c) => setCompletedCrop(c)}
                                aspect={aspect}
                                minHeight={100}
                                keepSelection
                            >
                                <img
                                    ref={imgRef}
                                    alt="Crop"
                                    src={imgSrc}
                                    onLoad={onImageLoad}
                                />
                            </ReactCrop>
                        </div>
                    )}
                    {!!completedCrop && (
                        <div className={styles.cropImage} >
                            <div className={styles.preview} style={{ display: 'none' }}>
                                <canvas
                                    ref={previewCanvasRef}
                                    style={{
                                        border: "1px solid black",
                                        objectFit: "contain",
                                        width: completedCrop.width,
                                        height: completedCrop.height,
                                    }}
                                />
                            </div>
                            <span className={styles.changePhoto} onClick={() => setImgSrc('')}>Remove Photo</span>
                            <Button type='Primary' text='Continue' onClick={onSaveCrop} className={styles.confirm} />
                        </div>
                    )}
                </>)
            }
        </>
    );
};

export default UploadBox;
